/**
 * THE SAF-T(AO) MANDATORY FIELDS, AS THE WEB NAMES THEM — design U8 item 33 a)
 * (`docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3; owner "go" 2026-09-25): "each a named refusal
 * the builder shows before send/confirm/print".
 *
 * ===========================================================================
 * TWO HALVES, ONE VOCABULARY
 * ===========================================================================
 * The API refuses `send`, `confirm` and `/pdf` with 422 `VALIDATION_FAILED` and
 * `details.reason = 'SAFT_FIELD_MISSING'`, naming the fields in a STABLE shape —
 * `party.tax_no`, `party.address`, `party.city`, `line[3].product_code`,
 * `line[3].description` (`apps/api/src/modules/quotes/assert-saft-fields.ts`). The mobile
 * app will branch on the same shape later (the app is untouched in U8; owner: phone after).
 *
 *   · {@link saftFieldsSentence} turns THAT list into the plain sentence the record page
 *     shows when a send or confirm is refused — the server's message names the mechanism
 *     ("under the SAF-T(AO) regime"), and the operator needs the fields.
 *   · {@link missingSaftFieldsReason} is the BUILDER'S half: the same rule, mirrored over the
 *     party and lines the builder already holds, so the sentence appears before the first
 *     request rather than after a refusal. It mirrors the two party-and-line rules the
 *     builder CAN see — a blank NIF / address / city, and a description over 200 characters.
 *     NOT U3's party LENGTH rungs: the server judges those only at a freeze that issues an AGT
 *     number (design §3 U3, G1), and the web does not know the regime — so the builder never
 *     blocks on length, and the sentence names each bound for when that refusal arrives. A blank
 *     product code is a catalogue fact the picker cannot produce, so it is left to the
 *     server; the refusal sentence above still names it if it ever arrives.
 *
 * KEYED ON THE COUNTRY PROFILE'S `saftAo` — the mirror of migration 67's row flag
 * (`domain/countries.ts`, held to the seed by its parity test). Never on `code === 'AO'`.
 *
 * NO MONEY: the sentences name fields, never a value. `999999999` is SAF-T(AO)'s generic
 * consumer id — a customer with no NIF is recorded with it, on purpose, and it passes.
 */

import { countryProfile, isCountryCode } from './countries';

/** SAF-T(AO)'s generic consumer tax id — accepted exactly as typed on the party. */
export const SAFT_GENERIC_CONSUMER_TAX_ID = '999999999';

/** The XSD's `ProductDescription` ceiling. */
export const SAFT_DESCRIPTION_MAX = 200;

/** Design §3 U3 — the customer's XSD bounds, held at write (the party forms) and at an AGT freeze. */
export const SAFT_CITY_MAX = 50;
export const SAFT_ADDRESS_MAX = 250;
export const SAFT_TAX_ID_MAX = 30;
/** Design §3 U6 — `Telephone` and `Province` (`parties.state`): optional, held at write; the export refuses a longer one. */
export const SAFT_PHONE_MAX = 20;
export const SAFT_PROVINCE_MAX = 50;

/** What the builder can judge: the party's three fields, and each line's description. */
export interface SaftPartyLike {
  readonly tax_no?: string | null;
  readonly address?: string | null;
  readonly city?: string | null;
}

const isBlank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '';

/** A field path from the API's `details.fields`, in the operator's words. */
function describeField(path: string): string {
  // Blank OR (at an AGT freeze) over the XSD bound — the server uses the one path for both.
  if (path === 'party.tax_no') {
    return `the customer's NIF (or ${SAFT_GENERIC_CONSUMER_TAX_ID} for a final consumer; not longer than ${String(SAFT_TAX_ID_MAX)} characters)`;
  }
  if (path === 'party.address') return `the customer's address (not longer than ${String(SAFT_ADDRESS_MAX)} characters)`;
  if (path === 'party.city') return `the customer's city (not longer than ${String(SAFT_CITY_MAX)} characters)`;
  const line = /^line\[(\d+)\]\.(product_code|description)$/.exec(path);
  if (line !== null) {
    return line[2] === 'product_code'
      ? `a product code on line ${line[1]}`
      : `a shorter description on line ${line[1]} (at most ${String(SAFT_DESCRIPTION_MAX)} characters)`;
  }
  // A path this build has never heard of (U3 skipped unit of measure and the exemption
  // reason, ⚑): shown as the server spelt it rather than hidden.
  return path;
}

/**
 * The record page's sentence for a refused send/confirm/print — from the API's own field
 * list. Empty or absent list: the mechanism sentence would be all there is, so a generic
 * one is returned instead of an empty string.
 */
export function saftFieldsSentence(fields: unknown): string {
  const list = Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
  if (list.length === 0) {
    return 'Angola’s SAF-T file needs fields this document does not yet carry. Complete the customer record and the lines, then try again.';
  }
  return `Angola’s SAF-T file needs ${joinWithAnd(list.map(describeField))}. Add ${
    list.length === 1 ? 'it' : 'them'
  } before this document can be sent, confirmed or printed.`;
}

/**
 * The builder's sentence, or `null` when nothing is missing or the country is outside
 * the regime. `lines` carry only the description the operator typed; a blank one is the
 * catalogue's name and is never over length.
 */
export function missingSaftFieldsReason(
  countryCode: string,
  party: SaftPartyLike | null,
  lines: ReadonlyArray<{ readonly description: string }>,
): string | null {
  if (!isCountryCode(countryCode) || !countryProfile(countryCode).saftAo) return null;

  const fields: string[] = [];
  if (party !== null) {
    if (isBlank(party.tax_no)) fields.push('party.tax_no');
    if (isBlank(party.address)) fields.push('party.address');
    if (isBlank(party.city)) fields.push('party.city');
  }
  lines.forEach((line, index) => {
    if (line.description.length > SAFT_DESCRIPTION_MAX) fields.push(`line[${String(index + 1)}].description`);
  });
  if (fields.length === 0) return null;

  const partyFields = fields.filter((f) => f.startsWith('party.'));
  const sentence = saftFieldsSentence(fields);
  // The builder can point at the screen; the record page's sentence cannot presume one.
  return partyFields.length > 0 ? `${sentence} Add the customer’s details on the party record.` : sentence;
}

function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
